"use client";
import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import { ReactSVG } from "react-svg";
import "./Footer.scss";
import { TextField, Tooltip } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { subscribeToNewsletter } from "@/util/newsletter";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import ReCAPTCHA from "react-google-recaptcha";
import { recaptchaValidate } from "@/utils/recaptchaValidate";
// import PaymentsButton from "../PaymentsButton/PaymentsButton";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const { recaptchaRef, handleRecaptchaChange, reset, verifyToken } = useRecaptcha();

  const isEmailValid = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEmailValid(email)) {
      toast.error("Please enter a valid email address.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Validate reCAPTCHA before sending
    const isRecaptchaValid = await recaptchaValidate(verifyToken);
    if (!isRecaptchaValid) return;

    // Send to Mailchimp via server route (double opt-in via status 'pending')
    try {
      await subscribeToNewsletter({ email });
    } catch (err: any) {
      toast.error(err?.message || "Subscription failed.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    toast.success("Subscription received. Please check your email to confirm.", {
      position: "top-right",
      autoClose: 3000,
    });

    setEmail("");
    setTouched(false);
    reset();
  };

  return (
    <div className=' bg-neutral-50'>
      <div className="footer-wrapper ">
        <div className='first-row baseContainer '>
          <div className='row-wrapper maxWidth'>
            <div className='section-wrapper'>
              <ReactSVG
                src='/assets/secure-ic.svg'
                role="img"
                aria-label="Secure Payments"
              />
              <p className='text-xsmall-18 white'>Secure Payments</p>
            </div>

            <div className='section-wrapper'>
              <ReactSVG
                src='/assets/users-heart-ic.svg'
                role="img"
                aria-label="Family Owned & Operated"
              />
              <p className='text-xsmall-18 white'>Family Owned & Operated</p>
            </div>

            <div className='section-wrapper'>
              <ReactSVG
                src='/assets/building-ic.svg'
                role="img"
                aria-label="Established in 2010"
              />
              <p className='text-xsmall-18 white'>Established in 2010</p>
            </div>

            <div className='section-wrapper'>
              <Image
                src='/assets/castle-logo.png'
                width={58}
                height={33}
                alt='Castle logo'
              />
              <p className='text-xsmall-18 white'>Castle Member</p>
            </div>
          </div>
        </div>

        <div className=' baseContainer bg-[#6e7ea6] w-full'>
          <div className="second-row maxWidth">
            <p className='text-medium-24 white'>Sign up for our monthly flyer</p>

            <form onSubmit={handleSubmit} className='email-subscription-form'>
              <TextField
                label='Email'
                variant='outlined'
                type='email'
                name='email'
                placeholder='Enter your email'
                value={email}
                onChange={handleEmailChange}
                onBlur={handleBlur}
                error={touched && !isEmailValid(email)}
                helperText={
                  touched && !isEmailValid(email) ? "Invalid email address" : ""
                }
                InputProps={{
                  endAdornment: <ReactSVG src='/assets/mail-ic.svg' />,
                }}
              />
              {/* Add reCAPTCHA below the email input and above the button */}
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                onChange={handleRecaptchaChange}
                className='recaptcha-wrapper'
              />
              <button
                type='submit'
                className='main-btn light-blue'
                disabled={!isEmailValid(email)}>
                Subscribe
              </button>
            </form>
          </div>

        </div>
        <div className="baseContainer w-full">
          <div className='footer maxWidth'>
            <div className='wrap'>
              <div className='upper'>
                <div className='left'>
                  <Image
                    src={"/assets/logo.webp"}
                    alt='Amherst Supply logo'
                    width={319}
                    height={100}
                  />
                  <div className='list'>
                    <div className='footer-info-wrapper'>
                      <ReactSVG
                        src='/assets/location-ic.svg'
                        role="img"
                        aria-label="Location"
                      />
                      <a
                        href='https://maps.app.goo.gl/oc6zY4rkfBgxkTe47'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-mini-14 weight-600'
                        aria-label='1500 Alma Street, Amherstburg, ON N9V 2Y9 (opens in a new tab)'>
                        1500 Alma Street, Amherstburg, ON N9V 2Y9
                      </a>
                    </div>
                    <div className='footer-info-wrapper'>
                      <ReactSVG src='/assets/mail-ic.svg' role="img" aria-label="Mail" />
                      <a
                        className='text-mini-14 weight-600'
                        href='mailto:hello@amherstsupply.com'
                        aria-label='Email Amherst Supply'>
                        hello@amherstsupply.com
                      </a>
                    </div>
                    <div className='footer-info-wrapper'>
                      <ReactSVG src='/assets/call-ic.svg' role="img" aria-label="Call" />
                      <a
                        href='tel:+15197139318'
                        className='text-mini-14 weight-600'
                        aria-label='Call Amherst Supply at (519) 713-9318'>
                        (519) 713-9318
                      </a>
                    </div>
                  </div>
                </div>

                <div className='right'>
                  <div className='box'>
                    <p className='text-xsmall-18 blue-500 weight-500'>General</p>
                    <Link className='text-mini-14 weight-600' href={"/"}>
                      Home
                    </Link>
                    <Link className='text-mini-14 weight-600' href={"/about"}>
                      About
                    </Link>
                    <Link className='text-mini-14 weight-600' href={"/contact"}>
                      Contact
                    </Link>
                    <a className='text-mini-14 weight-600' href={"/careers"}>
                      Careers
                    </a>
                  </div>

                  <div className='box'>
                    <p className='text-xsmall-18 blue-500 weight-500'>Services</p>
                    <Link className='text-mini-14 weight-600' href={"/services"}>
                      Our Services
                    </Link>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/flexiti-financing"}>
                      Flexiti Financial
                    </Link>
                    <Link className='text-mini-14 weight-600' href={"/chickens"}>
                      Chick Orders
                    </Link>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/building-packages"}>
                      Building Packages
                    </Link>
                    {/* <PaymentsButton variant="footer" /> */}
                  </div>

                  <div className='box'>
                    <p className='text-xsmall-18 blue-500 weight-500'>
                      Contractors
                    </p>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/account-applications"}>
                      Account Applications
                    </Link>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/magazine-subscription"}
                      rel='noopener noreferrer'>
                      Magazine Subscription
                    </Link>
                    <Link className='text-mini-14 weight-600' href={"/Referral"}>
                      Referral Form
                    </Link>
                  </div>

                  <div className='box'>
                    <p className='text-xsmall-18 blue-500 weight-500'>Policies</p>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/store-policy"}>
                      Store Policies
                    </Link>
                    <Link className='text-mini-14 weight-600' href={"/terms"}>
                      Terms of Service
                    </Link>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/privacy-policy"}>
                      Privacy Policy
                    </Link>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/shipping-policy"}>
                      Shipping Policy
                    </Link>
                    <Link
                      className='text-mini-14 weight-600'
                      href={"/refund-policy"}>
                      Refund Policy
                    </Link>
                  </div>

                  <div className='box follow-us'>
                    <p className='text-xsmall-18 blue-500 weight-500'>Follow Us</p>
                    <div className='row'>
                      <Link
                        href={"https://www.facebook.com/amherstsupply"}
                        target='_blank'
                        aria-label='Facebook (opens in a new tab)'>
                        <ReactSVG
                          src='/assets/fb-ic.svg'
                          role="img"
                          aria-label="Facebook"
                        />
                      </Link>
                      <Link
                        href={
                          "https://www.instagram.com/amherstsupply.castlebuilding/"
                        }
                        target='_blank'
                        aria-label='Instagram (opens in a new tab)'>
                        <ReactSVG
                          src='/assets/instagram-ic.svg'
                          role="img"
                          aria-label="Instagram"
                        />
                      </Link>
                      <Link
                        href={
                          "https://ca.linkedin.com/company/amherst-supply-ltd?trk=public_profile_topcard-current-company"
                        }
                        target='_blank'
                        aria-label='LinkedIn (opens in a new tab)'>
                        <ReactSVG
                          src='/assets/linkedin-ic.svg'
                          role="img"
                          aria-label="LinkedIn"
                        />
                      </Link>
                      <Link
                        href={
                          "https://www.youtube.com/channel/UCNJjOnXACrM1a4vA4jRa6kw"
                        }
                        target='_blank'
                        aria-label='YouTube (opens in a new tab)'>
                        <ReactSVG
                          src='/assets/yt-ic.svg'
                          role="img"
                          aria-label="YouTube"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className='images-wrapper'>
                <Tooltip title='MasterCard' arrow>
                  <img
                    className='pay-ic'
                    src='/assets/master-card-ic.svg'
                    alt='MasterCard'
                  />
                </Tooltip>
                <Tooltip title='Visa' arrow>
                  <img className='pay-ic' src='/assets/visa-ic.svg' alt='Visa' />
                </Tooltip>
                <Tooltip title='Interac - E-Transfer' arrow>
                  <img className='pay-img' src='/assets/pay3.webp' alt='Interac' />
                </Tooltip>
                <Tooltip title='Electronic funds transfers' arrow>
                  <div className='method-wrapper'>
                    <Image src='/assets/eft.png' alt='EFT' fill />
                  </div>
                </Tooltip>
              </div>
              <div className='lower'>
                <p className='text-12'>
                  © Copyright © 2024 Amherst Supply, All Rights Reserved.
                </p>
                <p className='text-12'>
                  Designed & Developed by{" "}
                  <a
                    href='https://webelocity.io'
                    target='_blank'
                    rel='noopener noreferrer'>
                    Webelocity
                  </a>
                </p>

                <div className='powered-by-wrapper'>
                  <p className='text-12'>Powered by</p>
                  <ReactSVG
                    src='/assets/toolswift-ic.svg'
                    role="img"
                    aria-label="ToolSwift"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
      <ToastContainer />

    </div>
  );
};

export default Footer;
